import axios, { InternalAxiosRequestConfig } from "axios";
import { paths } from "utilities/globals";
import { snakeCase } from "utilities/json";
import { squish } from "utilities/strings";

declare module "axios" {
  interface AxiosRequestConfig {
    skipInflection?: boolean;
  }
}

const csrfTag = document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null;

const client = axios.create({
  headers: {
    common: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Key-Inflection": "camel",
      "X-Requested-With": "XMLHttpRequest",
      ...(csrfTag && { "X-CSRF-Token": csrfTag.content }),
    },
  },
});

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.skipInflection) {
    config.headers.delete("Key-Inflection");
  }

  if (config.data instanceof FormData) {
    config.headers.delete("Content-Type");
  }

  if (config.params) {
    return {
      ...config,
      params: snakeCase(config.params),
    };
  }

  return config;
});

// The consortium magic-link error pages (/consortium/{draw,roster-confirmation}/{invalid,expired})
// are public and reached by users who could NOT be signed in. Their boot-time 401s must not bounce
// the visitor to sign_in, or the error/resend page never renders.
const CONSORTIUM_PUBLIC_ERROR_PATH =
  /\/consortium\/(?:draw|roster-confirmation)\/(?:invalid|expired)/;

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (CONSORTIUM_PUBLIC_ERROR_PATH.test(window.location.pathname)) {
        return Promise.reject(
          new axios.Cancel(
            squish(
              `401 (Unauthorized): ${error.response.config.url}. Suppressed on public consortium error page.`
            )
          )
        );
      }

      window.location.href = paths.sign_in;

      return Promise.reject(
        new axios.Cancel(
          squish(`
            ${error.response.status} (Unauthorized):
            ${error.response.config.url}. Redirecting to ${paths.sign_in}.
          `)
        )
      );
    }

    return Promise.reject(error);
  }
);

export default client;
